import { base64ToBytes, bytesToBase64, randomBytes, utf8ToBytes } from './bytes.ts';
import { PBKDF2_ITERATIONS } from './aes.ts';

export const AUTH_INFO = 'farfar-auth-v1';
export const WRAP_INFO = 'farfar-wrap-v1';
const HKDF_SALT = utf8ToBytes('farfar-hkdf-extract-v1');

export type PasswordKdfPublic = {
  v: 1;
  hash: 'SHA-256';
  iterations: number;
  salt: string;
};

export type DerivedPasswordKeys = {
  authKey: string;
  wrapKey: CryptoKey;
  wrapKeyRaw: Uint8Array;
};

export function newPasswordKdf(): PasswordKdfPublic {
  return {
    v: 1,
    hash: 'SHA-256',
    iterations: PBKDF2_ITERATIONS,
    salt: bytesToBase64(randomBytes(16))
  };
}

export async function derivePasswordKeys(
  password: string,
  kdf: PasswordKdfPublic
): Promise<DerivedPasswordKeys> {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    utf8ToBytes(password),
    'PBKDF2',
    false,
    ['deriveBits']
  );
  const ikm = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      hash: kdf.hash,
      iterations: kdf.iterations,
      salt: base64ToBytes(kdf.salt)
    },
    baseKey,
    256
  );
  const hkdf = await crypto.subtle.importKey('raw', ikm, 'HKDF', false, ['deriveBits', 'deriveKey']);
  const authBits = await crypto.subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: HKDF_SALT, info: utf8ToBytes(AUTH_INFO) },
    hkdf,
    256
  );
  const wrapKey = await crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: HKDF_SALT, info: utf8ToBytes(WRAP_INFO) },
    hkdf,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
  return {
    authKey: bytesToBase64(new Uint8Array(authBits)),
    wrapKey,
    wrapKeyRaw: new Uint8Array(await crypto.subtle.exportKey('raw', wrapKey))
  };
}

export async function importWrapKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']);
}
